import { useEffect, useRef, type RefObject } from 'react'
import { CH, WATER, MOUNTAIN, TRACK_SPACING, TRAIN_PERIOD, TRAIN_SPEED, TRAIN_CARS, hash, trackV, groundHeight, generateChunk, type Building, type Chunk } from './terrain/world'

// The command-and-control field behind the sign-in screen: an isometric wireframe WORLD that the
// view drifts across toward the mouse (no drift over the console, base speed just outside it, twice
// that at the far edge). The world is infinite and deterministic (see terrain/world.ts), generated
// in 16×16 chunks, cached, and generated one chunk beyond the visible edge in the direction of
// travel so features exist before they scroll on: relief, lakes with boats, towns with buildings,
// streets and roads between them, rail corridors with bridges and running trains, trees, relay
// towers, mountain ranges with named peaks. A faint pulse pushes out from the center every few
// seconds; as it passes it brightens the mesh and lights towns, towers and peaks with their
// callsigns. Canvas, DPR-aware, one requestAnimationFrame loop, colors from skin tokens only.
// Nothing here is data. Honors prefers-reduced-motion (still frame: no drift, no pulse, no motion).

interface Palette {
  line: string
  lineStrong: string
  glow: string
  glowSoft: string
  glowFaint: string
  blip: string
  blipWarm: string
  text: string
  mono: string
}

function readPalette(el: HTMLElement): Palette {
  const s = getComputedStyle(el)
  const v = (name: string) => s.getPropertyValue(name).trim()
  return {
    line: v('--radar-line'),
    lineStrong: v('--radar-line-strong'),
    glow: v('--radar-glow'),
    glowSoft: v('--radar-glow-soft'),
    glowFaint: v('--radar-glow-faint'),
    blip: v('--radar-blip'),
    blipWarm: v('--radar-blip-warm'),
    text: v('--radar-text-dim'),
    mono: v('--font-mono') || 'monospace',
  }
}

interface Pulse { u: number; v: number; born: number }

/** anchor: the element the drift is measured from (the console). */
export function TerrainField({ anchor }: { anchor?: RefObject<HTMLElement | null> } = {}) {
  const ref = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = ref.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    const palette = readPalette(canvas)
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches

    const CELL = 34
    const ISO_X = Math.cos(Math.PI / 6)
    const ISO_Y = 0.5
    const ZS = 0.55
    const PULSE_EVERY = 5200
    const PULSE_SPEED = 7.5
    const PULSE_BAND = 3.0
    const MAX_DRIFT = 0.9
    const CACHE_MAX = 420

    const off = document.createElement('canvas')
    const octx = off.getContext('2d')!
    let raf = 0
    let w = 0
    let h = 0
    let dpr = 1
    let ox = 0
    let oy = 0
    let vx = 0
    let vy = 0
    let mx = 0
    let my = 0
    let last = performance.now()
    let nextPulse = last + 900
    const pulses: Pulse[] = []
    const lit = new Map<string, number>()
    const chunks = new Map<string, Chunk>()
    const key = (cu: number, cv: number) => cu + ',' + cv

    const chunkAt = (cu: number, cv: number): Chunk => {
      const k = key(cu, cv)
      let c = chunks.get(k)
      if (!c) { c = generateChunk(cu, cv); chunks.set(k, c) }
      return c
    }
    const heightAt = (u: number, v: number): number => {
      const cu = Math.floor(u / CH)
      const cv = Math.floor(v / CH)
      return chunkAt(cu, cv).h[(v - cv * CH) * (CH + 1) + (u - cu * CH)]
    }
    const isWater = (u: number, v: number): boolean => {
      const cu = Math.floor(u / CH)
      const cv = Math.floor(v / CH)
      return chunkAt(cu, cv).water[(v - cv * CH) * CH + (u - cu * CH)] === 1
    }
    const interiorWater = (u: number, v: number): boolean => isWater(u, v) && isWater(u - 1, v) && isWater(u, v - 1) && isWater(u - 1, v - 1)
    const heightAtF = (u: number, v: number): number => {
      const u0 = Math.floor(u)
      const v0 = Math.floor(v)
      const tu = u - u0
      const tv = v - v0
      const a = heightAt(u0, v0), b = heightAt(u0 + 1, v0), c = heightAt(u0, v0 + 1), d = heightAt(u0 + 1, v0 + 1)
      return (a + (b - a) * tu) * (1 - tv) + (c + (d - c) * tu) * tv
    }

    const resize = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2)
      w = canvas.clientWidth
      h = canvas.clientHeight
      canvas.width = Math.floor(w * dpr)
      canvas.height = Math.floor(h * dpr)
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      off.width = canvas.width
      off.height = canvas.height
      octx.setTransform(dpr, 0, 0, dpr, 0, 0)
    }
    const onMove = (e: MouseEvent) => {
      // direction: from the anchor's center to the pointer, in true screen pixels. speed: 0 over
      // the anchor, 1× just outside its edge, 2× at the farthest point of the screen.
      const r = anchor?.current?.getBoundingClientRect()
      const cx = r ? r.left + r.width / 2 : w / 2
      const cy = r ? r.top + r.height / 2 : h / 2
      const hw = r ? r.width / 2 : 0
      const hh = r ? r.height / 2 : 0
      const dx = e.clientX - cx
      const dy = e.clientY - cy
      const outside = Math.hypot(Math.max(0, Math.abs(dx) - hw), Math.max(0, Math.abs(dy) - hh))
      if (outside <= 0) { mx = 0; my = 0; return }
      const reach = Math.max(1, Math.hypot(Math.max(cx, w - cx) - hw, Math.max(cy, h - cy) - hh))
      const factor = 1 + Math.min(1, outside / reach)
      const len = Math.hypot(dx, dy) || 1
      mx = (dx / len) * factor
      my = (dy / len) * factor
    }
    const onLeave = () => { mx = 0; my = 0 }

    const project = (u: number, v: number, z: number): [number, number] => {
      const du = u - ox
      const dv = v - oy
      return [w / 2 + (du - dv) * CELL * ISO_X, h / 2 + (du + dv) * CELL * ISO_Y - z * CELL * ZS]
    }
    const edgeFade = (sx: number, sy: number) => {
      const nx = (sx - w / 2) / (w * 0.55)
      const ny = (sy - h / 2) / (h * 0.62)
      return Math.max(0, Math.min(1, 1.15 - Math.sqrt(nx * nx + ny * ny)))
    }
    const poly = (c: CanvasRenderingContext2D, pts: [number, number][], close = false) => {
      pts.forEach(([x, y], i) => (i ? c.lineTo(x, y) : c.moveTo(x, y)))
      if (close) c.closePath()
    }

    const draw = (now: number) => {
      const dt = Math.min(64, now - last) / 1000
      last = now

      if (!reduced) {
        const tu = (mx / ISO_X + my / ISO_Y) / 2
        const tv = (my / ISO_Y - mx / ISO_X) / 2
        const mag = Math.hypot(mx, my)
        const scale = mag > 0 ? Math.min(2, mag) * MAX_DRIFT / Math.max(1e-6, Math.hypot(tu, tv)) : 0
        const ease = 1 - Math.exp(-dt * 0.9)
        vx += (tu * scale - vx) * ease
        vy += (tv * scale - vy) * ease
        ox += vx * dt
        oy += vy * dt
        if (now >= nextPulse) {
          pulses.push({ u: ox, v: oy, born: now })
          nextPulse = now + PULSE_EVERY + Math.random() * 1800
        }
      }
      while (pulses.length && ((now - pulses[0].born) / 1000) * PULSE_SPEED > 64) pulses.shift()

      ctx.clearRect(0, 0, w, h)

      const N = Math.max(Math.ceil(w / (CELL * ISO_X) / 2) + 6, Math.ceil(h / (CELL * ISO_Y) / 2) + 12)
      const u0 = Math.round(ox)
      const v0 = Math.round(oy)

      // ── anticipation: generate the ring of chunks beyond the visible edge, travel side first
      {
        const cu0 = Math.floor((u0 - N) / CH) - 1, cu1 = Math.floor((u0 + N) / CH) + 1
        const cv0 = Math.floor((v0 - N) / CH) - 1, cv1 = Math.floor((v0 + N) / CH) + 1
        let budget = 2
        const order: [number, number, number][] = []
        for (let cu = cu0; cu <= cu1; cu++)
          for (let cv = cv0; cv <= cv1; cv++)
            if (!chunks.has(key(cu, cv))) {
              const du = cu * CH + CH / 2 - ox, dv = cv * CH + CH / 2 - oy
              order.push([du * vx + dv * vy > 0 ? 0 : 1, Math.hypot(du, dv), cu * 100000 + cv])
            }
        order.sort((a, b) => a[0] - b[0] || a[1] - b[1])
        for (const [, , packed] of order) {
          if (budget-- <= 0) break
          const cu = Math.floor(packed / 100000)
          chunkAt(cu, packed - cu * 100000)
        }
        if (chunks.size > CACHE_MAX) {
          const far = [...chunks.values()].sort((a, b) => Math.hypot(b.cu * CH - ox, b.cv * CH - oy) - Math.hypot(a.cu * CH - ox, a.cv * CH - oy))
          for (let i = 0; i < chunks.size - CACHE_MAX; i++) chunks.delete(key(far[i].cu, far[i].cv))
        }
      }

      const radii = pulses.map((p) => ({ p, r: ((now - p.born) / 1000) * PULSE_SPEED }))
      const pulseAt = (u: number, v: number) => {
        let best = 0
        for (const { p, r } of radii) {
          const d = Math.abs(Math.hypot(u - p.u, v - p.v) - r)
          if (d < PULSE_BAND) best = Math.max(best, 0.5 * (1 + Math.cos((Math.PI * d) / PULSE_BAND)) * Math.max(0, 1 - r / 60))
        }
        return best
      }
      const litness = (name: string, u: number, v: number, life: number) => {
        if (pulseAt(u, v) > 0.35) lit.set(name, now)
        const since = lit.has(name) ? (now - lit.get(name)!) / 1000 : Infinity
        return reduced ? 0.3 : Math.max(0, 1 - since / life)
      }

      const vcu0 = Math.floor((u0 - N) / CH), vcu1 = Math.floor((u0 + N) / CH)
      const vcv0 = Math.floor((v0 - N) / CH), vcv1 = Math.floor((v0 + N) / CH)
      const visible: Chunk[] = []
      for (let cu = vcu0; cu <= vcu1; cu++) for (let cv = vcv0; cv <= vcv1; cv++) visible.push(chunkAt(cu, cv))
      visible.sort((a, b) => a.cu + a.cv - (b.cu + b.cv))

      // ── lakes: visible flat fill with a slow shimmer, stroked shoreline, drifting ripples
      const lakeCells: [number, number, number][] = []
      for (const c of visible)
        for (let lv = 0; lv < CH; lv++)
          for (let lu = 0; lu < CH; lu++) {
            if (!c.water[lv * CH + lu]) continue
            const u = c.cu * CH + lu, v = c.cv * CH + lv
            const [ax, ay] = project(u + 0.5, v + 0.5, WATER)
            const f = edgeFade(ax, ay)
            if (f > 0.02) lakeCells.push([u, v, f])
          }
      ctx.fillStyle = palette.glowSoft
      for (const [u, v, f] of lakeCells) {
        ctx.globalAlpha = f * (0.5 + 0.18 * Math.sin(now / 1400 + u * 0.9 + v * 0.6))
        ctx.beginPath(); poly(ctx, [project(u, v, WATER), project(u + 1, v, WATER), project(u + 1, v + 1, WATER), project(u, v + 1, WATER)], true); ctx.fill()
      }
      ctx.strokeStyle = palette.lineStrong
      ctx.lineWidth = 1.1
      ctx.beginPath()
      for (const [u, v] of lakeCells) {
        if (!isWater(u, v - 1)) { const [ax, ay] = project(u, v, WATER); const [bx, by] = project(u + 1, v, WATER); ctx.moveTo(ax, ay); ctx.lineTo(bx, by) }
        if (!isWater(u, v + 1)) { const [ax, ay] = project(u, v + 1, WATER); const [bx, by] = project(u + 1, v + 1, WATER); ctx.moveTo(ax, ay); ctx.lineTo(bx, by) }
        if (!isWater(u - 1, v)) { const [ax, ay] = project(u, v, WATER); const [bx, by] = project(u, v + 1, WATER); ctx.moveTo(ax, ay); ctx.lineTo(bx, by) }
        if (!isWater(u + 1, v)) { const [ax, ay] = project(u + 1, v, WATER); const [bx, by] = project(u + 1, v + 1, WATER); ctx.moveTo(ax, ay); ctx.lineTo(bx, by) }
      }
      ctx.globalAlpha = 0.9
      ctx.stroke()
      ctx.lineWidth = 1
      ctx.beginPath()
      for (const [u, v, f] of lakeCells) {
        if (hash(u, v, 51) > 0.13 || f < 0.15) continue
        const [sx, sy] = project(u + 0.5, v + 0.5, WATER)
        const dx = reduced ? 0 : Math.sin(now / 1600 + hash(u, v, 52) * 6.28) * 5
        const len = 6 + hash(u, v, 53) * 8
        ctx.moveTo(sx + dx - len / 2, sy); ctx.lineTo(sx + dx + len / 2, sy)
      }
      ctx.globalAlpha = 0.45
      ctx.stroke()
      ctx.globalAlpha = 1

      // ── base mesh: land and shoreline vertices only (the mesh dissolves inside lakes); mountains
      //    get a second, slightly stronger stroke on their high vertices so ridgelines stand out
      const strokeMesh = (c: CanvasRenderingContext2D, keep?: (u: number, v: number) => boolean) => {
        c.lineJoin = 'round'
        for (let pass = 0; pass < 2; pass++)
          for (let i = -N; i <= N; i++) {
            c.beginPath()
            let open = false
            for (let j = -N; j <= N; j++) {
              const u = pass === 0 ? u0 + i : u0 + j
              const v = pass === 0 ? v0 + j : v0 + i
              if (keep && !keep(u, v)) { open = false; continue }
              const [sx, sy] = project(u, v, heightAt(u, v))
              if (!open) { c.moveTo(sx, sy); open = true } else c.lineTo(sx, sy)
            }
            c.stroke()
          }
      }
      const edgeMask = (c: CanvasRenderingContext2D) => {
        const mask = c.createRadialGradient(w / 2, h / 2, Math.min(w, h) * 0.18, w / 2, h / 2, Math.max(w, h) * 0.62)
        mask.addColorStop(0, 'rgba(0,0,0,0)')
        mask.addColorStop(1, 'rgba(0,0,0,1)')
        c.save(); c.globalCompositeOperation = 'destination-out'; c.fillStyle = mask; c.fillRect(0, 0, w, h); c.restore()
      }
      ctx.lineWidth = 1
      ctx.strokeStyle = palette.line
      strokeMesh(ctx, (u, v) => !interiorWater(u, v))
      ctx.lineWidth = 1.2
      ctx.strokeStyle = palette.lineStrong
      ctx.globalAlpha = 0.55
      strokeMesh(ctx, (u, v) => heightAt(u, v) > MOUNTAIN)
      ctx.globalAlpha = 1
      edgeMask(ctx)

      // ── the pulse: mesh in glow on an offscreen canvas, masked to a soft ring (world circle →
      //    screen ellipse) whose alpha rises and falls smoothly across the band
      for (const { p, r } of radii) {
        const decay = Math.max(0, 1 - r / 60)
        if (decay <= 0) continue
        octx.clearRect(0, 0, w, h)
        octx.lineWidth = 1.3
        octx.strokeStyle = palette.glow
        const lo = r - PULSE_BAND - 1.5, hi = r + PULSE_BAND + 1.5
        strokeMesh(octx, (u, v) => { const d = Math.hypot(u - p.u, v - p.v); return d >= lo && d <= hi && !interiorWater(u, v) })
        const [pcx, pcy] = project(p.u, p.v, heightAtF(p.u, p.v))
        const ax = r * CELL * ISO_X * Math.SQRT2
        const band = PULSE_BAND * CELL * ISO_X * Math.SQRT2
        const inner = Math.max(0, ax - band), outer = ax + band
        octx.save()
        octx.globalCompositeOperation = 'destination-in'
        octx.translate(pcx, pcy)
        octx.scale(1, ISO_Y / ISO_X)
        const ring = octx.createRadialGradient(0, 0, inner, 0, 0, outer)
        const mid = (ax - inner) / (outer - inner)
        ring.addColorStop(0, 'rgba(0,0,0,0)')
        ring.addColorStop(Math.max(0.001, mid * 0.5), 'rgba(0,0,0,0.3)')
        ring.addColorStop(mid, 'rgba(0,0,0,1)')
        ring.addColorStop(Math.min(0.999, mid + (1 - mid) * 0.5), 'rgba(0,0,0,0.3)')
        ring.addColorStop(1, 'rgba(0,0,0,0)')
        octx.fillStyle = ring
        octx.fillRect(-w * 2, -h * 4, w * 4, h * 8)
        octx.restore()
        edgeMask(octx)
        ctx.globalAlpha = decay * 0.6
        ctx.drawImage(off, 0, 0, off.width, off.height, 0, 0, w, h)
        ctx.globalAlpha = 1
      }

      // ── roads and streets, on the terrain
      const strokeWorldLine = (pts: [number, number][], lift: number, style: string, width: number, alphaScale: number) => {
        ctx.beginPath()
        let f = 0
        pts.forEach(([u, v], i) => {
          const [sx, sy] = project(u, v, heightAtF(u, v) + lift)
          f = Math.max(f, edgeFade(sx, sy))
          if (i === 0) ctx.moveTo(sx, sy); else ctx.lineTo(sx, sy)
        })
        if (f <= 0.02) return
        ctx.strokeStyle = style
        ctx.lineWidth = width
        ctx.globalAlpha = f * alphaScale
        ctx.stroke()
      }
      for (const c of visible) {
        for (const road of c.roads) strokeWorldLine(road, 0.03, palette.lineStrong, 1.1, 0.7)
        for (const street of c.streets) strokeWorldLine(street, 0.03, palette.lineStrong, 1.2, 0.9)
      }
      ctx.globalAlpha = 1

      // ── rail: two rails, ties every cell, piers where the line bridges water
      for (const c of visible)
        for (const run of c.tracks) {
          if (run.pts.length < 2) continue
          const left: [number, number][] = [], right: [number, number][] = []
          let f = 0
          for (const p of run.pts) {
            const [lx, ly] = project(p.u, p.v - 0.14, p.z + 0.04)
            const [rx, ry] = project(p.u, p.v + 0.14, p.z + 0.04)
            left.push([lx, ly]); right.push([rx, ry])
            f = Math.max(f, edgeFade(lx, ly))
          }
          if (f <= 0.02) continue
          ctx.strokeStyle = palette.lineStrong
          ctx.globalAlpha = f * 0.85
          ctx.lineWidth = 1
          ctx.beginPath(); poly(ctx, left); poly(ctx, right); ctx.stroke()
          ctx.globalAlpha = f * 0.5
          ctx.beginPath()
          for (const p of run.pts) {
            const [ax, ay] = project(p.u, p.v - 0.3, p.z + 0.04)
            const [bx, by] = project(p.u, p.v + 0.3, p.z + 0.04)
            ctx.moveTo(ax, ay); ctx.lineTo(bx, by)
            if (p.bridge) { const [gx, gy] = project(p.u, p.v, WATER); ctx.moveTo(bx, by); ctx.lineTo(gx, gy) }
          }
          ctx.stroke()
        }
      ctx.globalAlpha = 1

      // ── trains: a few cars on each corridor, a fixed distance apart, moving along u
      if (visible.length) {
        const kk0 = Math.floor((v0 - N) / TRACK_SPACING), kk1 = Math.floor((v0 + N) / TRACK_SPACING)
        const t = reduced ? 0 : now / 1000
        for (let k = kk0; k <= kk1; k++) {
          const head = (t * TRAIN_SPEED + k * 173) % TRAIN_PERIOD
          const m0 = Math.floor((u0 - N - head) / TRAIN_PERIOD), m1 = Math.floor((u0 + N - head) / TRAIN_PERIOD)
          for (let m = m0; m <= m1; m++) {
            const uHead = m * TRAIN_PERIOD + head
            for (let car = 0; car < TRAIN_CARS; car++) {
              const u = uHead - car * 1.1
              const v = trackV(u, k)
              const g = groundHeight(u, v)
              const z = (g <= WATER ? WATER + 0.35 : Math.min(g, MOUNTAIN)) + 0.05
              const [sx, sy] = project(u, v, z)
              const f = edgeFade(sx, sy)
              if (f <= 0.02) continue
              const zh = car === 0 ? 0.5 : 0.38
              const b: [number, number][] = [[u - 0.5, v - 0.18], [u + 0.5, v - 0.18], [u + 0.5, v + 0.18], [u - 0.5, v + 0.18]]
              const base = b.map(([a, c2]) => project(a, c2, z))
              const top = b.map(([a, c2]) => project(a, c2, z + zh))
              ctx.fillStyle = palette.glowFaint
              ctx.globalAlpha = f
              ctx.beginPath(); poly(ctx, top, true); ctx.fill()
              ctx.strokeStyle = car === 0 ? palette.blipWarm : palette.blip
              ctx.lineWidth = 1
              ctx.globalAlpha = f * 0.9
              ctx.beginPath(); poly(ctx, top, true)
              for (const i of [1, 2, 3]) { ctx.moveTo(base[i][0], base[i][1]); ctx.lineTo(top[i][0], top[i][1]) }
              ctx.moveTo(base[1][0], base[1][1]); ctx.lineTo(base[2][0], base[2][1]); ctx.lineTo(base[3][0], base[3][1])
              ctx.stroke()
              if (car === 0) {
                const [hx, hy] = project(u + 0.5, v, z + zh * 0.6)
                ctx.fillStyle = palette.blipWarm; ctx.shadowColor = palette.blipWarm; ctx.shadowBlur = 8
                ctx.beginPath(); ctx.arc(hx, hy, 1.6, 0, Math.PI * 2); ctx.fill(); ctx.shadowBlur = 0
              }
            }
          }
        }
        ctx.globalAlpha = 1
      }

      // ── trees
      ctx.strokeStyle = palette.lineStrong
      ctx.lineWidth = 1
      for (const c of visible)
        for (const tr of c.trees) {
          const z = heightAtF(tr.u, tr.v)
          const [bx, by] = project(tr.u, tr.v, z)
          const f = edgeFade(bx, by)
          if (f <= 0.02) continue
          const [tx, ty] = project(tr.u, tr.v, z + 0.45)
          ctx.globalAlpha = f * 0.8
          ctx.beginPath(); ctx.moveTo(bx, by); ctx.lineTo(tx, ty)
          ctx.moveTo(tx - 3, ty + 4); ctx.lineTo(tx, ty - 3); ctx.lineTo(tx + 3, ty + 4); ctx.closePath()
          ctx.stroke()
        }
      ctx.globalAlpha = 1

      // ── buildings: wireframe boxes, far → near
      const boxes: Building[] = []
      for (const c of visible) boxes.push(...c.buildings)
      boxes.sort((a, b) => a.u + a.du / 2 + a.v + a.dv / 2 - (b.u + b.du / 2 + b.v + b.dv / 2))
      for (const b of boxes) {
        const [cx0, cy0] = project(b.u + b.du / 2, b.v + b.dv / 2, b.z)
        const f = edgeFade(cx0, cy0)
        if (f <= 0.02) continue
        const k = pulseAt(b.u, b.v)
        const corners: [number, number][] = [[b.u, b.v], [b.u + b.du, b.v], [b.u + b.du, b.v + b.dv], [b.u, b.v + b.dv]]
        const base = corners.map(([u, v]) => project(u, v, b.z))
        const top = corners.map(([u, v]) => project(u, v, b.z + b.zh))
        ctx.globalAlpha = f * (0.5 + k * 0.5)
        ctx.fillStyle = palette.glowFaint
        ctx.beginPath(); poly(ctx, top, true); ctx.fill()
        ctx.strokeStyle = k > 0.05 ? palette.glow : palette.lineStrong
        ctx.lineWidth = 1
        ctx.globalAlpha = f * (0.75 + k * 0.25)
        ctx.beginPath(); poly(ctx, top, true)
        for (const i of [1, 2, 3]) { ctx.moveTo(base[i][0], base[i][1]); ctx.lineTo(top[i][0], top[i][1]) }
        ctx.moveTo(base[1][0], base[1][1]); ctx.lineTo(base[2][0], base[2][1]); ctx.lineTo(base[3][0], base[3][1])
        ctx.stroke()
      }
      ctx.globalAlpha = 1

      // ── boats: a hull, a mast, a wake; drifting slowly on their heading and bobbing
      for (const c of visible)
        for (const bt of c.boats) {
          const t = reduced ? 0 : now / 1000
          const along = Math.sin(t / 7 + bt.phase) * 0.9
          const u = bt.u + Math.cos(bt.heading) * along
          const v = bt.v + Math.sin(bt.heading) * along
          const z = WATER + 0.05 + (reduced ? 0 : Math.sin(t * 1.3 + bt.phase) * 0.03)
          const [sx, sy] = project(u, v, z)
          const f = edgeFade(sx, sy)
          if (f <= 0.02) continue
          const ca = Math.cos(bt.heading), sa = Math.sin(bt.heading)
          const hull: [number, number][] = [[0.55, 0], [0.25, 0.18], [-0.45, 0.18], [-0.55, 0], [-0.45, -0.18], [0.25, -0.18]]
            .map(([a, b]) => project(u + a * ca - b * sa, v + a * sa + b * ca, z) as [number, number])
          ctx.fillStyle = palette.glowFaint
          ctx.globalAlpha = f
          ctx.beginPath(); poly(ctx, hull, true); ctx.fill()
          ctx.strokeStyle = palette.blip
          ctx.lineWidth = 1
          ctx.globalAlpha = f * 0.85
          ctx.beginPath(); poly(ctx, hull, true)
          const [mx0, my0] = project(u, v, z)
          const [mx1, my1] = project(u, v, z + 0.7)
          ctx.moveTo(mx0, my0); ctx.lineTo(mx1, my1)
          ctx.stroke()
          const dir = Math.sign(Math.cos(t / 7 + bt.phase)) || 1
          ctx.strokeStyle = palette.lineStrong
          ctx.globalAlpha = f * 0.35
          ctx.beginPath()
          for (const side of [-1, 1]) {
            const [ax, ay] = project(u - dir * ca * 0.5, v - dir * sa * 0.5, WATER)
            const [bx, by] = project(u - dir * ca * 1.8 - side * sa * 0.35, v - dir * sa * 1.8 + side * ca * 0.35, WATER)
            ctx.moveTo(ax, ay); ctx.lineTo(bx, by)
          }
          ctx.stroke()
          ctx.fillStyle = palette.blipWarm
          ctx.globalAlpha = f * (0.5 + 0.5 * Math.max(0, Math.sin(t * 2 + bt.phase)))
          ctx.beginPath(); ctx.arc(mx1, my1, 1.4, 0, Math.PI * 2); ctx.fill()
        }
      ctx.globalAlpha = 1

      // ── targets: a faint resting diamond; as the pulse passes, a stalk to the ground, a ground
      //    ring that opens, a bracket that closes in, and the contact id, all fading over a few seconds
      ctx.font = `10px ${palette.mono}`
      ctx.textAlign = 'left'
      ctx.textBaseline = 'middle'
      for (const c of visible)
        for (const tg of c.targets) {
          const zg = heightAtF(tg.u, tg.v)
          const [gx, gy] = project(tg.u, tg.v, zg)
          const f = edgeFade(gx, gy)
          if (f <= 0.02) continue
          const g = litness(tg.name, tg.u, tg.v, 3.4)
          const [sx, sy] = project(tg.u, tg.v, zg + 0.9)
          const color = tg.warm ? palette.blipWarm : palette.blip
          ctx.globalAlpha = f * (0.22 + g * 0.78)
          ctx.fillStyle = color
          ctx.shadowColor = color
          ctx.shadowBlur = 10 * g
          ctx.beginPath()
          ctx.moveTo(sx, sy - 4); ctx.lineTo(sx + 4, sy); ctx.lineTo(sx, sy + 4); ctx.lineTo(sx - 4, sy); ctx.closePath()
          ctx.fill()
          ctx.shadowBlur = 0
          if (g > 0.02) {
            ctx.strokeStyle = color
            ctx.lineWidth = 1
            ctx.globalAlpha = f * g * 0.85
            ctx.beginPath(); ctx.moveTo(sx, sy); ctx.lineTo(gx, gy); ctx.stroke()
            const rr = 0.4 + (1 - g) * 1.6
            ctx.beginPath()
            for (let a = 0; a <= 28; a++) {
              const tt = (a / 28) * Math.PI * 2
              const [px, py] = project(tg.u + Math.cos(tt) * rr, tg.v + Math.sin(tt) * rr, zg)
              if (a === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py)
            }
            ctx.stroke()
            const b = 9 + (1 - g) * 7
            ctx.beginPath()
            ctx.moveTo(sx - b, sy - b + 4); ctx.lineTo(sx - b, sy - b); ctx.lineTo(sx - b + 4, sy - b)
            ctx.moveTo(sx + b - 4, sy - b); ctx.lineTo(sx + b, sy - b); ctx.lineTo(sx + b, sy - b + 4)
            ctx.moveTo(sx - b, sy + b - 4); ctx.lineTo(sx - b, sy + b); ctx.lineTo(sx - b + 4, sy + b)
            ctx.moveTo(sx + b - 4, sy + b); ctx.lineTo(sx + b, sy + b); ctx.lineTo(sx + b, sy + b - 4)
            ctx.stroke()
            ctx.fillStyle = color
            ctx.globalAlpha = f * g
            ctx.fillText(tg.name, sx + b + 5, sy - b + 3)
          }
        }
      ctx.globalAlpha = 1

      // ── landmarks and towns: lit by the pulse, callsign while lit
      for (const c of visible) {
        const L = c.landmark
        if (L && L.kind === 'tower') {
          const [bx, by] = project(L.u, L.v, L.z)
          const f = edgeFade(bx, by)
          if (f > 0.02) {
            const [tx, ty] = project(L.u, L.v, L.z + 3.6)
            const [ax, ay] = project(L.u, L.v, L.z + 4.4)
            const g = litness(L.name, L.u, L.v, 3.2)
            ctx.strokeStyle = g > 0.05 ? palette.blipWarm : palette.lineStrong
            ctx.globalAlpha = f * (0.7 + g * 0.3)
            ctx.lineWidth = 1
            ctx.beginPath()
            ctx.moveTo(bx - 5, by); ctx.lineTo(tx - 2, ty); ctx.moveTo(bx + 5, by); ctx.lineTo(tx + 2, ty)
            for (let r = 1; r <= 3; r++) { const yy = by + (ty - by) * (r / 3.2); const hw = 5 - r; ctx.moveTo(bx - hw, yy); ctx.lineTo(bx + hw, yy) }
            ctx.moveTo(tx, ty); ctx.lineTo(ax, ay)
            ctx.stroke()
            const blink = reduced ? 0.6 : 0.35 + 0.65 * Math.max(0, Math.sin(now / 700 + L.u))
            ctx.fillStyle = palette.blipWarm; ctx.shadowColor = palette.blipWarm; ctx.shadowBlur = 8 * blink
            ctx.globalAlpha = f * blink
            ctx.beginPath(); ctx.arc(ax, ay, 1.8, 0, Math.PI * 2); ctx.fill()
            ctx.shadowBlur = 0
            if (g > 0.05) { ctx.globalAlpha = f * g; ctx.fillStyle = palette.blipWarm; ctx.fillText(L.name, ax + 8, ay) }
          }
        }
        if (L && L.kind === 'peak') {
          const [sx, sy] = project(L.u, L.v, L.z)
          const f = edgeFade(sx, sy)
          if (f > 0.02) {
            const g = litness(L.name, L.u, L.v, 3.6)
            ctx.strokeStyle = g > 0.05 ? palette.blip : palette.lineStrong
            ctx.globalAlpha = f * (0.6 + g * 0.4)
            ctx.lineWidth = 1
            ctx.beginPath()
            ctx.moveTo(sx - 5, sy - 3); ctx.lineTo(sx, sy - 11); ctx.lineTo(sx + 5, sy - 3); ctx.closePath()
            ctx.moveTo(sx, sy - 11); ctx.lineTo(sx, sy - 18)
            ctx.stroke()
            if (g > 0.05) { ctx.globalAlpha = f * g; ctx.fillStyle = palette.blip; ctx.fillText(L.name, sx + 8, sy - 16) }
          }
        }
        if (c.town) {
          const T = c.town
          const z = heightAtF(T.u + 0.5, T.v + 0.5)
          const [sx, sy] = project(T.u + 0.5, T.v + 0.5, z)
          const f = edgeFade(sx, sy)
          if (f > 0.02) {
            const g = litness(T.name, T.u, T.v, 3.6)
            if (g > 0.02) {
              ctx.strokeStyle = palette.blip
              ctx.globalAlpha = f * g * 0.9
              ctx.lineWidth = 1
              const rr = 1.2 + (1 - g) * 2.4
              ctx.beginPath()
              for (let a = 0; a <= 32; a++) {
                const tt = (a / 32) * Math.PI * 2
                const [px, py] = project(T.u + 0.5 + Math.cos(tt) * rr, T.v + 0.5 + Math.sin(tt) * rr, z)
                if (a === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py)
              }
              ctx.stroke()
              const b = 14 + (1 - g) * 8
              ctx.beginPath()
              ctx.moveTo(sx - b, sy - b + 5); ctx.lineTo(sx - b, sy - b); ctx.lineTo(sx - b + 5, sy - b)
              ctx.moveTo(sx + b - 5, sy - b); ctx.lineTo(sx + b, sy - b); ctx.lineTo(sx + b, sy - b + 5)
              ctx.moveTo(sx - b, sy + b - 5); ctx.lineTo(sx - b, sy + b); ctx.lineTo(sx - b + 5, sy + b)
              ctx.moveTo(sx + b - 5, sy + b); ctx.lineTo(sx + b, sy + b); ctx.lineTo(sx + b, sy + b - 5)
              ctx.stroke()
              ctx.fillStyle = palette.blip
              ctx.globalAlpha = f * g
              ctx.fillText(T.name, sx + b + 6, sy - b + 4)
            }
          }
        }
      }
      ctx.globalAlpha = 1

      // ── center mark
      const [cx, cy] = project(ox, oy, heightAtF(ox, oy))
      ctx.fillStyle = palette.glow
      ctx.shadowColor = palette.glow
      ctx.shadowBlur = 14
      ctx.beginPath(); ctx.arc(cx, cy, 2.5, 0, Math.PI * 2); ctx.fill()
      ctx.shadowBlur = 0

      if (!reduced) raf = requestAnimationFrame(draw)
    }

    if (import.meta.env.DEV)
      (window as unknown as { __scoutField?: unknown }).__scoutField = {
        offset: () => [ox, oy],
        velocity: () => [vx, vy],
        teleport: (u: number, v: number) => { ox = u; oy = v },
        stats: () => {
          let cells = 0, water = 0, towns = 0, towers = 0, peaks = 0, buildings = 0, trees = 0, boats = 0, targets = 0, roads = 0, trackPts = 0
          let wettest: { cu: number; cv: number; water: number } | null = null
          let highest: { cu: number; cv: number; z: number } | null = null
          let boatChunk: { cu: number; cv: number } | null = null
          let trackChunk: { cu: number; cv: number } | null = null
          for (const c of chunks.values()) {
            cells += CH * CH; water += c.waterCount; towns += c.town ? 1 : 0; towers += c.landmark?.kind === 'tower' ? 1 : 0; peaks += c.landmark?.kind === 'peak' ? 1 : 0
            buildings += c.buildings.length; trees += c.trees.length; boats += c.boats.length; targets += c.targets.length; roads += c.roads.length; trackPts += c.tracks.reduce((n, r) => n + r.pts.length, 0)
            if (!wettest || c.waterCount > wettest.water) wettest = { cu: c.cu, cv: c.cv, water: c.waterCount }
            let z = 0; for (const x of c.h) if (x > z) z = x
            if (!highest || z > highest.z) highest = { cu: c.cu, cv: c.cv, z }
            if (!boatChunk && c.boats.length) boatChunk = { cu: c.cu, cv: c.cv }
            if (!trackChunk && c.tracks.some((r) => r.pts.some((p) => p.bridge))) trackChunk = { cu: c.cu, cv: c.cv }
          }
          return { chunks: chunks.size, waterFraction: water / cells, towns, towers, peaks, buildings, trees, boats, targets, roads, trackPts, wettest, highest, boatChunk, trackChunk }
        },
      }
    resize()
    const ro = new ResizeObserver(() => { resize(); if (reduced) draw(performance.now()) })
    ro.observe(canvas)
    window.addEventListener('mousemove', onMove)
    document.addEventListener('mouseleave', onLeave)
    raf = requestAnimationFrame(draw)
    return () => {
      cancelAnimationFrame(raf)
      ro.disconnect()
      window.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseleave', onLeave)
    }
  }, [anchor])

  return <canvas ref={ref} className="radar-field" aria-hidden="true" />
}
